import { ActionError, parseDetail } from '@shared/lib/api/errors';
import { apiPath } from '@shared/lib/config';
import { authHeaders } from './authToken';

/**
 * Authenticated fetch against the dashboard backend.
 *
 * Resolves `path` via apiPath(), merges Authorization from authHeaders(),
 * sets Content-Type when a JSON body is provided, and throws ActionError
 * (parsed from the {detail:{error_code,message}} envelope) on non-2xx.
 *
 * Returns the raw Response — callers extract .json() / .blob() / nothing
 * as needed for the endpoint.
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = { ...authHeaders() };
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const r = await fetch(apiPath(path), {
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!r.ok) {
    let detail = { error_code: null, message: null } as ReturnType<typeof parseDetail>;
    try {
      const body = await r.json();
      detail = parseDetail(body?.detail);
    } catch { /* non-JSON body */ }
    throw new ActionError(detail.error_code, detail.message, r.status);
  }
  return r;
}

/** POST with optional JSON body, expecting a JSON response. */
export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await apiFetch(path, init);
  return r.json();
}

/** GET expecting a JSON response. */
export async function apiGet<T>(path: string): Promise<T> {
  const r = await apiFetch(path);
  return r.json();
}
